import { Button, CircularProgress, Container, Stack } from "@mui/material";
import { Helmet } from "react-helmet-async";
import Input from "./input";
import HomePageEditorProvider, { useHomePageEditorContext } from "./provider";
import { StyledButtonContainer, StyledCardContainer, StyledContainer, StyledRow, StyledTypography, StyledTypographyDescription, StyledTypographyTitle, StyledUploadBox } from "./styles.css";
import UploadMask from "./upload-mask";
import { useServices } from "./hooks";
import { AlertType } from "@src/lib/types/toaster";
import { useToasterContext } from "@src/context/Toaster";
import { useEffect } from "react";
import Config from "@src/utils/api/config";
import { LoadingButton } from "@mui/lab";
import ContainerDialog from "./container-dialog";
import DescriptionDialog from "./description-dialog";
import SuccessDialog from "./success";

export default function HomePageEditor() {
	return (
		<HomePageEditorProvider>
		<>
			<Helmet>
				<title> Home Page Editor </title>
			</Helmet>
      <Component />
			<ContainerDialog />
			<DescriptionDialog />
			<SuccessDialog />
		</>
		</HomePageEditorProvider>
	)
}

function Component() {
	const { uploadImage } = useServices();
	const { triggerOpen } = useToasterContext();
	const { state, isLoading, isDirty, error, onDispatch, setContainer, setOpenDescription, onSubmit } = useHomePageEditorContext();
	
	const handleFileChange = (type: string) => (event: any) => {
		const file = event.target.files[0];
		if (file) {
			if (file.size > 3 * 1024 * 1024) {
				triggerOpen({
					type: AlertType.error,
					title: "Failed",
					message: "File size must be less than 3MB",
				});
				return;
			}

			const formData = new FormData();
			formData.append("file", file);
			formData.append("type", type);
			uploadImage.mutate(formData);
		}
	};

	useEffect(() => {
		if(uploadImage.data && uploadImage.isSuccess){
			onDispatch(uploadImage.data.type)(`${Config.APP_CDN_URL}/${uploadImage.data.result.filePath}`);   
		}
	},[uploadImage.isSuccess, uploadImage.data, onDispatch]); // eslint-disable-line react-hooks/exhaustive-deps

	return (
		<Container maxWidth="xl" >
			<Stack sx={{ width: "100%" }}>
				<Stack direction={"row"} justifyContent={"space-between"} alignItems={"center"} width={"auto"}>
					<StyledTypography >Main Banner</StyledTypography>
					<LoadingButton
						onClick={onSubmit}
						loading={isLoading}
						disabled={!isDirty}
						sx={{ width: "145px",
							height: "50px", borderRadius: 1,
							color: "white", bgcolor: isDirty ? "#8F17D9" : "#484848",
							alignSelf: "center", marginTop: 1.3,
							"&:hover": { bgcolor: isDirty ? "#7a12bb" : "#333" },
						}} >
						PUBLISH
					</LoadingButton>
				</Stack>
				<StyledUploadBox 
					sx={[{mt: 4 }, error["mainBlock.image"] && {border: "1px dashed red"}]} 
					>
					<input
						type="file"
						id="mainBlock.image"
						accept="image/*"
						style={{ display: 'none' }}
						onChange={handleFileChange("mainBlock.image")}
					/>
					{!state.mainBlock?.image && <UploadMask notes="Recommended size: 1085x300" 
						notesSx={{fontSize: 9}}
						notes2Sx={{fontSize: 9}}
						notes2="(recommended file type eg. .jpg, .png and file size 3mb or less)"/>}
					{uploadImage.isPending && 
						<CircularProgress sx={{position: "absolute"}} />}
					{state.mainBlock?.image && 
						<img 
							src={state.mainBlock?.image} 
							alt="Selected" 
							style={{ maxWidth: '90%', height: '90%' }} />}
				</StyledUploadBox>
				<LoadingButton  onClick={() => {
					document.getElementById('mainBlock.image')?.click();
				}} loading={uploadImage.isPending} sx={{ width: 230, 
					height: 40, borderRadius: 1, 
					color: "white", bgcolor: "#484848", 
					alignSelf: "center", marginTop: 3}} >UPLOAD IMAGE</LoadingButton>

				<Input
					disabled
					sx={{mt: 4}}
					minRows={4}
					inputSx={{
						"& .MuiOutlinedInput-root": {
							alignItems: "flex-start",
							padding: "12px 14px",
						},
						"& .MuiOutlinedInput-input": {
							fontSize: "15px",
							lineHeight: "22px",
							padding: 0,
						},
						"& .MuiInputBase-root.Mui-disabled": {
							opacity: 1,
							color: "black",
						},
						"& .MuiInputBase-input.Mui-disabled": {
							WebkitTextFillColor: "black",
						},
					}}
					placeholder='Promo description...'
					error={error["mainBlock.description"]}
					value={state?.mainBlock?.description}
					onChange={onDispatch("mainBlock.description")}
					/>
					<Button  onClick={() => setOpenDescription(true)} sx={{ width: 230, 
						height: 40, borderRadius: 1, 
						color: "white", bgcolor: "#484848", 
						alignSelf: "center", marginTop: 3}} >
						EDIT TEXT
					</Button>
				<StyledRow>
					<Stack alignItems={"center"} gap={2.5}>
						<StyledTypographyTitle>CONAINER 1</StyledTypographyTitle>
						<StyledCardContainer>
							<StyledContainer sx={[error["subBlock1.image"] && {border: "1px dashed red"}]} >
								<input
									type="file"
									id="subBlock1.image"
									accept="image/*"
									style={{ display: 'none' }}
									onChange={handleFileChange("subBlock1.image")}
								/>
								{!state.subBlock1?.image && <UploadMask notes="Recommended size: 440x256px"
									notesSx={{fontSize: 7.3}}
									notes2Sx={{fontSize: 6}}
									notes2="(recommended file type eg. .jpg, .png and file size 3mb or less)"/>}
								{uploadImage.isPending && 
									<CircularProgress sx={{position: "absolute"}} />}
								{state.subBlock1?.image && 
									<img 
										src={state.subBlock1?.image} 
										alt="Selected" 
										style={{ maxWidth: '90%', height: '90%' }} />}
							</StyledContainer>
							<StyledTypographyDescription>
								{state.subBlock1?.description || "Lorem ipsum dolor sit amet, consectetur adipiscing elit, sed do eiusmod tempor incididunt ut labore et dolore magna aliqua. Ut enim ad minim veniam, quis nostrud exercitation ullamco laboris nisi ut aliquip ex ea commodo consequat. Duis aute irure dolor in reprehenderit in voluptate velit esse cillum dolore eu fugiat nulla pariatur."}
							</StyledTypographyDescription>
						</StyledCardContainer>
						<StyledButtonContainer onClick={() => setContainer("subBlock1")}>
							UPDATE CONTAINER 1
						</StyledButtonContainer>
					</Stack>
					<Stack alignItems={"center"} gap={2.5}>
						<StyledTypographyTitle>CONAINER 2</StyledTypographyTitle>
						<StyledCardContainer>
							<StyledContainer sx={[error["subBlock2.image"] && {border: "1px dashed red"}]} >
								<input
									type="file"
									id="subBlock2.image"
									accept="image/*"
									style={{ display: 'none' }}
									onChange={handleFileChange("subBlock2.image")}
								/>
								{!state.subBlock2?.image && <UploadMask notes="Recommended size: 440x256px"
									notesSx={{fontSize: 7.3}}
									notes2Sx={{fontSize: 6}}
									notes2="(recommended file type eg. .jpg, .png and file size 3mb or less)"/>}
								{uploadImage.isPending && 
									<CircularProgress sx={{position: "absolute"}} />}
								{state.subBlock2?.image && 
									<img 
										src={state.subBlock2?.image} 
										alt="Selected" 
										style={{ maxWidth: '90%', height: '90%' }} />}
							</StyledContainer>
							<StyledTypographyDescription>
								{state.subBlock2?.description || "Lorem ipsum dolor sit amet, consectetur adipiscing elit, sed do eiusmod tempor incididunt ut labore et dolore magna aliqua. Ut enim ad minim veniam, quis nostrud exercitation ullamco laboris nisi ut aliquip ex ea commodo consequat. Duis aute irure dolor in reprehenderit in voluptate velit esse cillum dolore eu fugiat nulla pariatur."}
							</StyledTypographyDescription>
						</StyledCardContainer>
						<StyledButtonContainer onClick={() => setContainer("subBlock2")}>
							UPDATE CONTAINER 2
						</StyledButtonContainer>
					</Stack>
					<Stack alignItems={"center"} gap={2.5}>
						<StyledTypographyTitle>CONAINER 3</StyledTypographyTitle>
						<StyledCardContainer>
							<StyledContainer 
								sx={[error["subBlock3.image"] && {border: "1px dashed red"}]} 
								>
								<input
									type="file"
									id="subBlock3.image"
									accept="image/*"
									style={{ display: 'none' }}
									onChange={handleFileChange("subBlock3.image")}
								/>
								{!state.subBlock3?.image && <UploadMask notes="Recommended size: 440x256px" 
									notesSx={{fontSize: 7.3}}
									notes2Sx={{fontSize: 6}}
									notes2="(recommended file type eg. .jpg, .png and file size 3mb or less)"/>}
								{uploadImage.isPending && 
									<CircularProgress sx={{position: "absolute"}} />}
								{state.subBlock3?.image && 
									<img 
										src={state.subBlock3?.image} 
										alt="Selected" 
										style={{ maxWidth: '90%', height: '90%' }} />}
							</StyledContainer>
							<StyledTypographyDescription>
								{state.subBlock3?.description || "Lorem ipsum dolor sit amet, consectetur adipiscing elit, sed do eiusmod tempor incididunt ut labore et dolore magna aliqua. Ut enim ad minim veniam, quis nostrud exercitation ullamco laboris nisi ut aliquip ex ea commodo consequat. Duis aute irure dolor in reprehenderit in voluptate velit esse cillum dolore eu fugiat nulla pariatur."}
							</StyledTypographyDescription>
						</StyledCardContainer>
						<StyledButtonContainer onClick={() => setContainer("subBlock3")}>
							UPDATE CONTAINER 3
						</StyledButtonContainer>
					</Stack>
				</StyledRow>
				<StyledRow sx={{alignItems: "center"}}>
					<Input 
						label="CTA Text:"
						sx={{mt: 4}}
						inputSx={{
							'& .MuiInputBase-root': {
								width: '258px',
							},
						}}
						placeholder='ENTER NOW'
						error={error["cta.text"]}
						value={state?.cta?.text}
						onChange={onDispatch("cta.text")} />
					<Input 
						label="CTA Link:"
						sx={{mt: 4}}
						inputSx={{
							'& .MuiInputBase-root': {
								width: '258px',
							},
						}}
						placeholder='www.palmsplay.com'
						error={error["cta.link"]}
						value={state?.cta?.link}
						onChange={onDispatch("cta.link")} />
					<Stack sx={{ mt:2.5, width: '258px', gap: 0.5}}>
						<StyledTypography>CTA Preview:</StyledTypography>
						<Button
							sx={{
								background: "linear-gradient(180deg, #DF7B0B 0%, #E5D33D 100%)",
								color: "#000",
								fontWeight: 700,
								textTransform: "none",
								px: 3,
								py: 1.2,
								borderRadius: "7px",
								transition: "0.3s",

								"&:active": {
									transform: "scale(0.98)",
								},
							}}
						>
							{state?.cta?.text || "CTA TEXT"}
						</Button>
					</Stack>
				</StyledRow>
			</Stack>
		</Container>
	)
}